import {announce} from '@github-ui/aria-live'
import {getCurrentUserLogin, isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, Button, IconButton, Label, Link} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useRelayLiveUpdatesEnabled} from '../../contexts/relay-live-updates-context'
import {
  type ApplySuggestionRef,
  useApplyPendingIssueSuggestions,
} from '../../mutations/apply-pending-issue-suggestions-mutation'
import {
  type RejectSuggestionRef,
  useRejectPendingIssueSuggestions,
} from '../../mutations/reject-pending-issue-suggestions-mutation'
import {buildSuggestionRef} from '../../utils/suggestion-ref'
import styles from './RecentAgentActivity.module.css'
import {
  AppliedSuggestionChip,
  buildPendingChipItems,
  PendingLabelGroupChip,
  PendingSuggestionChip,
} from './SuggestionChip'
import {useDismissRecentAgentActivity} from './use-dismiss-recent-agent-activity'
import type {PendingSuggestion, RecentAgentActivityFragment} from './use-recent-agent-activity'
import {useRecentAgentActivity} from './use-recent-agent-activity'
import {RecentAgentActivitySubscriber} from './use-recent-agent-activity-subscription'

type RecentAgentActivityProps = {
  issue: RecentAgentActivityFragment
  issueBodyRef: React.RefObject<HTMLDivElement | null>
  issueNumber: number
  viewerId?: string
}

const FEEDBACK_URL = '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/orgs/community/discussions/202617'

function countByConfidence(suggestions: PendingSuggestion[]): {
  high_confidence_count: number
  medium_confidence_count: number
  low_confidence_count: number
  no_confidence_count: number
} {
  const counts = {high_confidence_count: 0, medium_confidence_count: 0, low_confidence_count: 0, no_confidence_count: 0}
  for (const suggestion of suggestions) {
    switch (suggestion.confidence) {
      case 'HIGH':
        counts.high_confidence_count++
        break
      case 'MEDIUM':
        counts.medium_confidence_count++
        break
      case 'LOW':
        counts.low_confidence_count++
        break
      default:
        counts.no_confidence_count++
    }
  }
  return counts
}

export function RecentAgentActivity({issue, issueBodyRef, issueNumber, viewerId}: RecentAgentActivityProps) {
  const relayLiveUpdatesEnabled = useRelayLiveUpdatesEnabled()
  const {viewerCanUpdateMetadata, issueId, repoNameWithOwner, pendingSuggestions} = useRecentAgentActivity(issue)
  const {isDismissed, dismissForIssue, dismissForever} = useDismissRecentAgentActivity(issueId)
  const {sendAnalyticsEvent} = useAnalytics()
  const [suggestionError, setSuggestionError] = useState<Error | null>(null)
  const [owner = '', repository = ''] = repoNameWithOwner.split('/')
  const applySuggestions = useApplyPendingIssueSuggestions({
    issueId,
    owner,
    repository,
    issueNumber,
    onError: setSuggestionError,
  })
  const rejectSuggestions = useRejectPendingIssueSuggestions({
    issueId,
    owner,
    repository,
    issueNumber,
    onError: setSuggestionError,
  })

  // The Relay viewer subscribes for authenticated users even when the panel is hidden. The Data
  // Router gets these updates through Alive, and anonymous users are refused by `issueUpdated`.
  // All return paths use a Fragment root so the subscriber remains mounted across panel states.
  const maybeSubscriber =
    relayLiveUpdatesEnabled && isLoggedIn() ? <RecentAgentActivitySubscriber issueId={issueId} /> : null

  const pendingSuggestionRows = useMemo(
    () => pendingSuggestions.groups.flatMap(group => group.suggestions.filter(s => s.state === 'PENDING')),
    [pendingSuggestions.groups],
  )
  const pendingCount = pendingSuggestionRows.length

  // Suggestions the viewer just approved in this session. Rendered as transient
  // applied chips so the viewer immediately sees the result of their action.
  // Cleared on unmount/refresh; the server's `pendingSuggestions` field only
  // returns PENDING rows (filtered in the hook), so applied chips never persist.
  // Keep overlapping batches separate so one failure cannot discard another batch's applied chips.
  const [approvalBatches, setApprovalBatches] = useState<Set<PendingSuggestion[]>>(() => new Set())
  const recentlyApproved = useMemo(
    () => [...new Map([...approvalBatches].flat().map(suggestion => [suggestion.id, suggestion])).values()],
    [approvalBatches],
  )
  const appliedCount = recentlyApproved.length

  const pendingChipItems = useMemo(() => buildPendingChipItems(pendingSuggestionRows), [pendingSuggestionRows])

  const panelRef = useRef<HTMLDivElement | null>(null)

  // After a chip is approved or rejected (individually or in bulk), move focus
  // to the next focusable chip in the list. If none remain, fall back to the
  // kebab "More actions" button, then to the issue body if the whole panel
  // has unmounted, so focus doesn't fall to document.body.
  const focusAfterAction = useCallback(() => {
    // Defer two frames so React can render the action's result.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const panel = panelRef.current
        if (!panel) {
          issueBodyRef.current?.focus()
          return
        }
        const nextChip = panel.querySelector<HTMLButtonElement>('[role="list"] button')
        if (nextChip) {
          nextChip.focus()
          return
        }
        const fallback = panel.querySelector<HTMLButtonElement>('[aria-label="More actions"]')
        fallback?.focus()
      })
    })
  }, [issueBodyRef])

  const showApprovedSuggestions = useCallback(
    (approved: PendingSuggestion[], message: string) => {
      setApprovalBatches(prev => new Set(prev).add(approved))
      announce(message)
      focusAfterAction()
    },
    [focusAfterAction],
  )

  const rollbackApprovedSuggestions = useCallback((approved: PendingSuggestion[]) => {
    setApprovalBatches(prev => {
      const next = new Set(prev)
      next.delete(approved)
      return next
    })
  }, [])

  const approveBatch = useCallback(
    (actorId: string, refs: ApplySuggestionRef[], suggestions: PendingSuggestion[], message: string) => {
      const approved = [...suggestions]
      const viewerLogin = getCurrentUserLogin()
      const approverSuggestions = suggestions.some(s => s.type === 'assignee_assigned' && s.assigneeIsAgent)
        ? pendingSuggestionRows.filter(
            s =>
              s.type === 'assignee_assigned' &&
              (s.assigneeId === viewerId || s.assigneeLogin === viewerLogin) &&
              !suggestions.some(selected => selected.id === s.id),
          )
        : []
      // The server auto-assigns the approver when accepting a coding agent, across suggesting actors.
      if (approverSuggestions[0]) approved.push(approverSuggestions[0])
      applySuggestions(
        actorId,
        refs,
        () => showApprovedSuggestions(approved, message),
        approverSuggestions.map(s => s.id),
        () => rollbackApprovedSuggestions(approved),
      )
    },
    [applySuggestions, pendingSuggestionRows, viewerId, showApprovedSuggestions, rollbackApprovedSuggestions],
  )

  const handleDismissForIssue = useCallback(() => {
    dismissForIssue()
    focusAfterAction()
  }, [dismissForIssue, focusAfterAction])

  const handleDismissForever = useCallback(() => {
    dismissForever()
    focusAfterAction()
  }, [dismissForever, focusAfterAction])

  const handleApprove = useCallback(
    (suggestion: PendingSuggestion) => {
      setSuggestionError(null)
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
        confidence: suggestion.confidence ?? 'NONE',
        intent_id: suggestion.intentId ?? 'NONE',
      })
      const ref = buildSuggestionRef(suggestion)
      if (!ref) return
      approveBatch(suggestion.actorId, [ref], [suggestion], 'Suggestion approved')
    },
    [approveBatch, sendAnalyticsEvent],
  )

  const handleReject = useCallback(
    (suggestion: PendingSuggestion) => {
      setSuggestionError(null)
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
        confidence: suggestion.confidence ?? 'NONE',
        intent_id: suggestion.intentId ?? 'NONE',
      })
      const ref = buildSuggestionRef(suggestion)
      if (!ref) return
      rejectSuggestions(suggestion.actorId, [ref], () => {
        announce('Suggestion rejected')
        focusAfterAction()
      })
    },
    [rejectSuggestions, sendAnalyticsEvent, focusAfterAction],
  )

  // Approve a group of pending suggestions that share an actor (e.g. multiple
  // labels suggested at the same confidence). We fire one mutation with all
  // the refs and an analytics event per suggestion so per-suggestion metrics
  // remain consistent with individual approvals.
  const handleApproveGroup = useCallback(
    (suggestionsToApprove: PendingSuggestion[]) => {
      if (suggestionsToApprove.length === 0) return
      setSuggestionError(null)
      const refs: ApplySuggestionRef[] = []
      const approved: PendingSuggestion[] = []
      for (const suggestion of suggestionsToApprove) {
        sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
          suggestion_type: suggestion.type,
          confidence: suggestion.confidence ?? 'NONE',
          intent_id: suggestion.intentId ?? 'NONE',
        })
        const ref = buildSuggestionRef(suggestion)
        if (ref) {
          refs.push(ref)
          approved.push(suggestion)
        }
      }
      if (refs.length === 0) return
      approveBatch(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        suggestionsToApprove[0]!.actorId,
        refs,
        approved,
        refs.length === 1 ? 'Suggestion approved' : `${refs.length} suggestions approved`,
      )
    },
    [approveBatch, sendAnalyticsEvent],
  )

  const handleRejectGroup = useCallback(
    (suggestionsToReject: PendingSuggestion[]) => {
      if (suggestionsToReject.length === 0) return
      setSuggestionError(null)
      const refs: RejectSuggestionRef[] = []
      for (const suggestion of suggestionsToReject) {
        sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
          suggestion_type: suggestion.type,
          confidence: suggestion.confidence ?? 'NONE',
          intent_id: suggestion.intentId ?? 'NONE',
        })
        const ref = buildSuggestionRef(suggestion)
        if (ref) refs.push(ref)
      }
      if (refs.length === 0) return
      rejectSuggestions(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        suggestionsToReject[0]!.actorId,
        refs,
        () => {
          announce(refs.length === 1 ? 'Suggestion rejected' : `${refs.length} suggestions rejected`)
          focusAfterAction()
        },
      )
    },
    [rejectSuggestions, sendAnalyticsEvent, focusAfterAction],
  )

  const handleApproveAll = useCallback(() => {
    setSuggestionError(null)
    sendAnalyticsEvent('issues.recent_agent_activity.suggestion_bulk_approved', 'RECENT_AGENT_ACTIVITY', {
      suggestion_count: pendingSuggestionRows.length,
      intent_ids: pendingSuggestionRows.map(s => s.intentId ?? 'NONE'),
      ...countByConfidence(pendingSuggestionRows),
    })
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {refs: ApplySuggestionRef[]; approved: PendingSuggestion[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildSuggestionRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {refs: [], approved: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.refs.push(ref)
      bucket.approved.push(suggestion)
    }
    for (const [actorId, {refs, approved}] of byActor) {
      approveBatch(
        actorId,
        refs,
        approved,
        approved.length === 1 ? 'Suggestion approved' : `${approved.length} suggestions approved`,
      )
    }
  }, [pendingSuggestionRows, approveBatch, sendAnalyticsEvent])

  const handleRejectAll = useCallback(() => {
    setSuggestionError(null)
    sendAnalyticsEvent('issues.recent_agent_activity.suggestion_bulk_rejected', 'RECENT_AGENT_ACTIVITY', {
      suggestion_count: pendingSuggestionRows.length,
      intent_ids: pendingSuggestionRows.map(s => s.intentId ?? 'NONE'),
      ...countByConfidence(pendingSuggestionRows),
    })
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {refs: RejectSuggestionRef[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildSuggestionRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {refs: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.refs.push(ref)
    }
    for (const [actorId, {refs}] of byActor) {
      rejectSuggestions(actorId, refs, () => {
        announce(refs.length === 1 ? 'Suggestion rejected' : `${refs.length} suggestions rejected`)
        focusAfterAction()
      })
    }
  }, [pendingSuggestionRows, rejectSuggestions, sendAnalyticsEvent, focusAfterAction])

  const hasFiredDisplayedRef = useRef(false)
  useEffect(() => {
    if (pendingCount > 0 && viewerCanUpdateMetadata && !isDismissed && !hasFiredDisplayedRef.current) {
      hasFiredDisplayedRef.current = true
      sendAnalyticsEvent('recent_agent_actions_panel.displayed', 'RECENT_AGENT_ACTIVITY', {
        action_count: pendingCount,
        agent_count: pendingSuggestions.groups.length,
      })
    }
  }, [pendingCount, viewerCanUpdateMetadata, isDismissed, sendAnalyticsEvent, pendingSuggestions.groups.length])

  if (pendingCount === 0 && appliedCount === 0) return <>{maybeSubscriber}</>
  if (!viewerCanUpdateMetadata || isDismissed) return <>{maybeSubscriber}</>

  // Require at least one identifiable agent — either from a still-pending group
  // or from a suggestion the viewer just approved (whose group has been
  // filtered out of `pendingSuggestions` once it transitioned to APPLIED).
  const hasAgent = pendingSuggestions.groups.some(g => g.agentSlug) || recentlyApproved.some(s => s.actorLogin)
  if (!hasAgent) return <>{maybeSubscriber}</>

  const hasPending = pendingCount > 0

  return (
    <>
      {maybeSubscriber}
      <div
        ref={panelRef}
        className={clsx(styles.container, isFeatureEnabled('issue_inline_avatars') && styles.container_inlineAvatars)}
        data-testid="recent-agent-activity"
      >
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <h2 className={styles.title}>Agent suggestions</h2>
            <Link href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer" className={styles.previewLink}>
              <Label variant="secondary">Public preview</Label>
            </Link>
          </div>
          <div className={styles.headerActions}>
            {hasPending && (
              <>
                <Button variant="default" size="small" onClick={handleRejectAll}>
                  Decline all
                </Button>
                <Button variant="primary" size="small" onClick={handleApproveAll}>
                  Accept all
                </Button>
              </>
            )}
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton icon={KebabHorizontalIcon} aria-label="More actions" size="small" variant="invisible" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>
                  <ActionList.LinkItem href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
                    Give feedback
                  </ActionList.LinkItem>
                  <ActionList.Item onSelect={handleDismissForIssue}>Dismiss</ActionList.Item>
                  <ActionList.Item onSelect={handleDismissForever}>Never show this again</ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </div>
        </div>
        {suggestionError && (
          <Banner variant="critical" role="alert">
            <Banner.Title>{suggestionError.message}</Banner.Title>
          </Banner>
        )}
        <div className={styles.chips} role="list" aria-label="Agent suggestions">
          {pendingChipItems.map(item => {
            if (item.kind === 'label-group') {
              return (
                <PendingLabelGroupChip
                  key={`pending-group-${item.key}`}
                  suggestions={item.suggestions}
                  onApprove={handleApproveGroup}
                  onReject={handleRejectGroup}
                />
              )
            }
            const {suggestion} = item
            const subjectKey =
              suggestion.issueTypeId ??
              suggestion.labelId ??
              suggestion.issueFieldId ??
              suggestion.assigneeId ??
              `${suggestion.type}:${suggestion.displayFieldName}`
            return (
              <PendingSuggestionChip
                key={`pending-${suggestion.actorId}:${subjectKey}`}
                suggestion={suggestion}
                repoNameWithOwner={repoNameWithOwner}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            )
          })}
          {hasPending && appliedCount > 0 && <span className={styles.divider} aria-hidden="true" />}
          {recentlyApproved.map(suggestion => {
            const subjectKey =
              suggestion.issueTypeId ??
              suggestion.labelId ??
              suggestion.issueFieldId ??
              suggestion.assigneeId ??
              `${suggestion.type}:${suggestion.displayFieldName}`
            return (
              <AppliedSuggestionChip
                key={`applied-${suggestion.actorId}:${subjectKey}`}
                suggestion={suggestion}
                repoNameWithOwner={repoNameWithOwner}
              />
            )
          })}
        </div>
      </div>
    </>
  )
}
